/**
 * http 通訊 protocol
 */

//-----------------------------------------------------------------------
// 載入模組

//-----------------------------------------------------------------------
// defines
import { CHANNEL_STATUS, COMMON_STATUS, MANUALLY_CHANNEL_STATUS, MEMBER_GROUP, COMMON_THREE_STATUS, TARGET_STATUS, MEMBER_PERMISSION, SCHEDULE_TASK, SCHEDULE_STATUS, EXTERNAL_TASK_TYPE, KEYWORD_SEARCH_MODE } from "./defines";
import { ApiRes, KeywordAliasInfo, KolTagInfo, MemberInfo, ParamTypeDef } from "./interfaces";
import { _AnyObject } from "./types";

//-----------------------------------------------------------------------
// 通訊協定。

/** 管理員相關 API。 */
export enum MEMBER_API
{
	/** 管理員相關 API 的上層路徑 */
	Path					= "/mbr",

	/** 若 session 還在登入狀態中可直接取得自己的資料。 */
	Me						= "/me",

	/** 用戶登入。 */
	Login					= "/login",

	/** 用戶登出。 */
	Logout					= "/logout",

	/** 取得管理員列表。 */
	List					= "/list",

	/** 新增管理員帳號。 */
	Create					= "/create",

	/** 取得管理員帳號資料。 */
	Get						= "/get",

	/** 修改管理員帳號資料。 */
	Modify					= "/modify",

	/** 刪除管理員帳號資料。 */
	Delete					= "/del",

	/** 取得管理員操作記錄列表。 */
	LogList					= "/log/list",

	/** 取得管理員權限與列表。 */
	PermissionList			= "/pm/list",
	PermissionGet			= "/pm/get",
}

/** 系統功能 API。 */
export enum SYSTEM_API
{
	/** 系統功能 API 的上層路徑 */
	Path					= "/sys",

	/** 資料來源相關功能。 */
	SourceList				= "/src/list",
	SourceGet				= "/src/get",
	SourceModify			= "/src/modify",

	/** 區域相關功能。 */
	RegionAll				= "/rgn/all/list",
	RegionAllGet			= "/rgn/all/get",
	RegionList				= "/rgn/list",
	RegionGet				= "/rgn/get",
	RegionModify			= "/rgn/modify",

	/** 關鍵字相關功能。 */
	KeywordAll				= "/kw/all/list",
	KeywordAllGet			= "/kw/all/get",
	KeywordList				= "/kw/list",
	KeywordCreate			= "/kw/create",
	KeywordGet				= "/kw/get",
	KeywordModify			= "/kw/modify",

	/** KOL 相關功能。 */
	KolAll					= "/kol/all/list",
	KolAllGet				= "/kol/all/get",
	KolList					= "/kol/list",
	KolCreate				= "/kol/create",
	KolGet					= "/kol/get",
	KolModify				= "/kol/modify",

	/** 手動新增頻道相關功能。 */
	ManuallyChannelList		= "/mch/list",
	ManuallyChannelCreate	= "/mch/create",
	ManuallyChannelGet		= "/mch/get",
	ManuallyChannelModify	= "/mch/modify",

	/** 排程相關功能。 */
	ScheduleList			= "/scd/list",
	ScheduleCreate			= "/scd/create",
	ScheduleGet				= "/scd/get",
	ScheduleModify			= "/scd/modify",

	/** 外部擴充相關功能。 */
	ExternalCheckTask		= "/ext/ct",
	ExternalPushData		= "/ext/pd",
}

/** 統計資料 API。 */
export enum STATISTICS_API
{
	/** 統計資料 API 的上層路徑 */
	Path					= "/sta",

	/** 頻道相關功能。 */
	ChannelList				= "/ch/list",
	ChannelGet				= "/ch/get",
	ChannelModify			= "/ch/modify",

	/** 目標相關功能。 */
	TargetList				= "/tg/list",
	TargetGet				= "/tg/get",
	TargetModify			= "/tg/modify",

	/** 排程目標追蹤相關功能。 */
	ScheduleTargetLogList	= "/stl/list",
	ScheduleTargetLogGet	= "/stl/get",

	/** 搜尋紀錄相關功能。 */
	SearchLogList			= "/sl/list",
}

//-----------------------------------------------------------------------
// 共用參數與回傳資料定義。

/** 取得列表：參數結構定義。 */
export const GET_LIST_PARAMS: ParamTypeDef[] =
[
	{ name: "page",		type: "integer",	acceptNull: false },
	{ name: "perPage",	type: "integer",	acceptNull: false },
	{ name: "sortKey",	type: "string",		acceptNull: false },
	{ name: "sortOrder",type: "string",		acceptNull: false },
	{ name: "filter",	type: "any",		acceptNull: false },
];
/** 取得列表：參數。 */
export interface GetListParams
{
	/** 讀取頁面。 */
	page				: number

	/** 每頁資料量。 */
	perPage				: number

	/** 排序依據欄位。 */
	sortKey				: string

	/** 排序次序。 */
	sortOrder			: 'ASC' | 'DESC'

	/** 資料過濾。 */
	filter				: { [ key: string ] : number | string }
}
/** 取得列表：結果。 */
export interface GetListRes<T> extends ApiRes
{
	/** 資料 array。 */
	list				: T[]

	/** 資料總數。 */
	total				: number
}

/** 取得資料：參數結構定義。 */
export const GET_DATA_PARAMS: ParamTypeDef[] =
[
	{ name: "id",		type: "any",		acceptNull: false },
	{ name: "filter",	type: "any",		acceptNull: true,	optional: true },
];
/** 取得資料：參數。 */
export interface GetDataParams
{
	/** 資料物件 ID。 */
	id					: number | string

	/** 資料過濾。 */
	filter				?: { [ key: string ] : number | string }
}
/** 取得資料：結果。 */
export interface GetDataRes<T> extends ApiRes
{
	/** 資料物件。 */
	data				: T
}

/** 建立資料：參數。 */
export interface CreateDataParams extends _AnyObject
{
}
/** 建立資料：結果。 */
export interface CreateDataRes extends ApiRes
{
	/** 資料物件 ID。 */
	id					: number | string

	/** 資料物件。 */
	data				: _AnyObject
}

/** 修改資料：參數。 */
export interface ModifyDataParams extends _AnyObject
{
	/** 資料物件 ID。 */
	id					: number | string
}
/** 修改資料：結果。 */
export interface ModifyDataRes extends ApiRes
{
	/** 資料物件 ID。 */
	id					: number | string

	/** 資料物件。 */
	data				: _AnyObject
}

/** 刪除資料：參數結構定義。 */
export const DELETE_DATA_PARAMS: ParamTypeDef[] =
[
	{ name: "id",		type: "any",		acceptNull: false },
	{ name: "filter",	type: "any",		acceptNull: true,	optional: true },
];
/** 刪除資料：參數。 */
export interface DeleteDataParams
{
	/** 資料物件 ID。 */
	id					: number | string

	/** 資料過濾。 */
	filter				?: { [ key: string ] : number | string }
}
/** 刪除資料：結果。 */
export interface DeleteDataRes extends ApiRes
{
	/** 資料物件 ID。 */
	id					: number | string
}

//-----------------------------------------------------------------------
// 管理員相關功能。

/** 管理員取得自己資料：結果。 */
export interface MemberMeRes extends ApiRes, MemberInfo { }

/** 管理員登入：參數結構定義。 */
export const MEMBER_LOGIN_PARAMS: ParamTypeDef[] =
[
	{ name: "email",	type: "string",		acceptNull: false },
	{ name: "passcode",	type: "string",		acceptNull: false },
];
/** 管理員登入：參數。 */
export interface MemberLoginParams
{
	/** 登入用 email。 */
	email				: string

	/** 登入驗證碼。 */
	passcode			: string
}
/** 管理員登入：結果。 */
export interface MemberLoginRes extends ApiRes, MemberInfo {}

/** 新建管理員帳號：參數結構定義。 */
export const MEMBER_CREATE_PARAMS: ParamTypeDef[] =
[
	{ name: "group",		type: "integer",	acceptNull: false },
	{ name: "permissions",	type: "array",		acceptNull: false },
	{ name: "name",			type: "string",		acceptNull: false },
	{ name: "email",		type: "string",		acceptNull: false },
	{ name: "passcode",		type: "string",		acceptNull: false },
	{ name: "status",		type: "string",		acceptNull: false },
];
/** 新建管理員帳號：參數。 */
export interface MemberCreateParams
{
	/** 所屬群組。 */
	group				: MEMBER_GROUP

	/** 管理員權限。 */
	permissions			: MEMBER_PERMISSION[]

	/** 顯示名稱。 */
	name				: string

	/** 登入用 email。 */
	email				: string

	/** 登入驗證碼。 */
	passcode			: string

	/** 帳號狀態。 */
	status				: COMMON_THREE_STATUS
}

/** 修改管理員帳號：參數結構定義。 */
export const MEMBER_MODIFY_PARAMS: ParamTypeDef[] =
[
	{ name: "id",			type: "integer",	acceptNull: false },
	{ name: "group",		type: "integer",	acceptNull: true,	optional: true },
	{ name: "permissions",	type: "array",		acceptNull: true,	optional: true },
	{ name: "name",			type: "string",		acceptNull: true,	optional: true },
	{ name: "email",		type: "string",		acceptNull: true,	optional: true },
	{ name: "passcode",		type: "string",		acceptNull: true,	optional: true },
	{ name: "status",		type: "string",		acceptNull: true,	optional: true },
];
/** 修改管理員帳號資料：參數。 */
export interface MemberModifyParams
{
	/** 目標管理員 ID。 */
	id					: number

	/** 所屬群組。 */
	group				?: MEMBER_GROUP

	/** 管理員權限。 */
	permissions			?: MEMBER_PERMISSION[]

	/** 顯示名稱。 */
	name				?: string

	/** 登入用 email。 */
	email				?: string

	/** 登入驗證碼。 */
	passcode			?: string

	/** 帳號狀態。 */
	status				?: COMMON_THREE_STATUS
}

//-----------------------------------------------------------------------
// 系統相關功能。

/** 新建資料來源：參數結構定義。 */
export const SOURCE_CREATE_PARAMS: ParamTypeDef[] =
[
	{ name: "name",		type: "string",		acceptNull: false },
	{ name: "url",		type: "string",		acceptNull: false },
	{ name: "status",	type: "string",		acceptNull: false },
];
/** 新建資料來源：參數。 */
export interface SourceCreateParams
{
	/** 名稱。 */
	name				: string

	/** 來源網站網址。 */
	url					: string

	/** 狀態。 */
	status				: COMMON_STATUS
}

/** 修改資料來源：參數結構定義。 */
export const SOURCE_MODIFY_PARAMS: ParamTypeDef[] =
[
	{ name: "id",		type: "integer",	acceptNull: false },
	{ name: "name",		type: "string",		acceptNull: true,	optional: true },
	{ name: "url",		type: "string",		acceptNull: true,	optional: true },
	{ name: "status",	type: "string",		acceptNull: true,	optional: true },
];
/** 修改資料來源：參數。 */
export interface SourceModifyParams
{
	/** 資料來源 ID。 */
	id					: number

	/** 名稱。 */
	name				?: string

	/** 來源網站網址。 */
	url					?: string

	/** 狀態。 */
	status				?: COMMON_STATUS
}

/** 修改區域：參數結構定義。 */
export const REGION_MODIFY_PARAMS: ParamTypeDef[] =
[
	{ name: "id",		type: "integer",	acceptNull: false },
	{ name: "status",	type: "string",		acceptNull: true,	optional: true },
];
/** 修改區域：參數。 */
export interface RegionModifyParams
{
	/** 區域 ID。 */
	id					: number

	/** 狀態。 */
	status				?: COMMON_STATUS
}

/** 新建關鍵字：參數結構定義。 */
export const KEYWORD_CREATE_PARAMS: ParamTypeDef[] =
[
	{ name: "text",				type: "string",		acceptNull: false },
	{ name: "limit",			type: "integer",	acceptNull: false },
	{ name: "searchMode",		type: "string",		acceptNull: false },
	{ name: "status",			type: "string",		acceptNull: false },
	{ name: "aliases",			type: "array",		acceptNull: true,	optional: true },
	{ name: "regions",			type: "array",		acceptNull: true,	optional: true },
	{ name: "excludeSources",	type: "array",		acceptNull: true,	optional: true },
];
/** 新建關鍵字：參數。 */
export interface KeywordCreateParams
{
	/** 關鍵字內容。 */
	text				: string

	/** 搜尋數量上限。 */
	limit				: number

	/** 搜尋模式。 */
	searchMode			: KEYWORD_SEARCH_MODE

	/** 狀態。 */
	status				: COMMON_STATUS

	/** 別名列表。 */
	aliases				?: KeywordAliasInfo[]

	/** 區域列表。 */
	regions				?: number[]

	/** 排除來源列表。 */
	excludeSources		?: number[]
}

/** 修改關鍵字：參數結構定義。 */
export const KEYWORD_MODIFY_PARAMS: ParamTypeDef[] =
[
	{ name: "id",				type: "integer",	acceptNull: false },
	{ name: "limit",			type: "integer",	acceptNull: true,	optional: true },
	{ name: "searchMode",		type: "string",		acceptNull: true,	optional: true },
	{ name: "status",			type: "string",		acceptNull: true,	optional: true },
	{ name: "aliases",			type: "array",		acceptNull: true,	optional: true },
	{ name: "regions",			type: "array",		acceptNull: true,	optional: true },
	{ name: "excludeSources",	type: "array",		acceptNull: true,	optional: true },
];
/** 修改關鍵字：參數。 */
export interface KeywordModifyParams
{
	/** 資料來源 ID。 */
	id					: number

	/** 搜尋數量上限。 */
	limit				?: number

	/** 搜尋模式。 */
	searchMode			?: KEYWORD_SEARCH_MODE

	/** 狀態。 */
	status				?: COMMON_STATUS

	/** 別名列表。 */
	aliases				?: KeywordAliasInfo[]

	/** 區域列表。 */
	regions				?: number[]

	/** 排除來源列表。 */
	excludeSources		?: number[]
}

/** 新建 KOL：參數結構定義。 */
export const KOL_CREATE_PARAMS: ParamTypeDef[] =
[
	{ name: "name",			type: "string",		acceptNull: false },
	{ name: "description",	type: "string",		acceptNull: false },
	{ name: "tags",			type: "array",		acceptNull: false },
	{ name: "status",		type: "string",		acceptNull: false },
];
/** 新建 KOL：參數。 */
export interface KolCreateParams
{
	/** 名稱。 */
	name				: string

	/** 描述。 */
	description			: string

	/** 標籤列表。 */
	tags				: KolTagInfo[]

	/** 狀態。 */
	status				: COMMON_THREE_STATUS
}

/** 修改 KOL：參數結構定義。 */
export const KOL_MODIFY_PARAMS: ParamTypeDef[] =
[
	{ name: "id",			type: "integer",	acceptNull: false },
	{ name: "name",			type: "string",		acceptNull: true,	optional: true },
	{ name: "description",	type: "string",		acceptNull: true,	optional: true },
	{ name: "tags",			type: "array",		acceptNull: true,	optional: true },
	{ name: "status",		type: "string",		acceptNull: true,	optional: true },
];
/** 修改 KOL：參數。 */
export interface KolModifyParams
{
	/** 資料來源 ID。 */
	id					: number

	/** 名稱。 */
	name				?: string

	/** 描述。 */
	description			?: string

	/** 標籤列表。 */
	tags				?: KolTagInfo[]

	/** 狀態。 */
	status				?: COMMON_THREE_STATUS
}

/** 新建手動新增頻道：參數結構定義。 */
export const MANUALLY_CHANNEL_CREATE_PARAMS: ParamTypeDef[] =
[
	{ name: "sourceId",	type: "integer",	acceptNull: false },
	{ name: "keywordId",type: "integer",	acceptNull: false },
	{ name: "url",		type: "string",		acceptNull: false },
];
/** 新建手動新增頻道：參數。 */
export interface ManuallyChannelCreateParams
{
	/** 資料來源 ID。 */
	sourceId			: number

	/** 關鍵字 ID。 */
	keywordId			: number

	/** 頻道網址。 */
	url					: string
}

/** 修改手動新增頻道：參數結構定義。 */
export const MANUALLY_CHANNEL_MODIFY_PARAMS: ParamTypeDef[] =
[
	{ name: "id",		type: "integer",	acceptNull: false },
	{ name: "sourceId",	type: "integer",	acceptNull: true,	optional: true },
	{ name: "keywordId",type: "integer",	acceptNull: true,	optional: true },
	{ name: "url",		type: "string",		acceptNull: true,	optional: true },
	{ name: "status",	type: "string",		acceptNull: true,	optional: true },
];
/** 修改手動新增頻道：參數。 */
export interface ManuallyChannelModifyParams
{
	/** 手動新增頻道 ID。 */
	id					: number

	/** 資料來源 ID。 */
	sourceId			?: number

	/** 關鍵字 ID。 */
	keywordId			?: number

	/** 頻道網址。 */
	url					?: string

	/** 狀態。 */
	status				?: MANUALLY_CHANNEL_STATUS
}

/** 新建排程工作：參數結構定義。 */
export const SCHEDULE_CREATE_PARAMS: ParamTypeDef[] =
[
	{ name: "task",			type: "string",		acceptNull: false },
	{ name: "parameters",	type: "string",		acceptNull: true,	optional: true },
	{ name: "time",			type: "string",		acceptNull: false },
];
/** 新建排程工作：參數。 */
export interface ScheduleCreateParams
{
	/** 工作種類。 */
	task				: SCHEDULE_TASK

	/** 工作參數。 */
	parameters			?: string

	/** 排定執行時間。 */
	time				: string
}

/** 修改排程工作：參數結構定義。 */
export const SCHEDULE_MODIFY_PARAMS: ParamTypeDef[] =
[
	{ name: "id",			type: "integer",	acceptNull: false },
	{ name: "task",			type: "string",		acceptNull: true,	optional: true },
	{ name: "parameters",	type: "string",		acceptNull: true,	optional: true },
	{ name: "time",			type: "string",		acceptNull: true,	optional: true },
	{ name: "status",		type: "string",		acceptNull: true,	optional: true },
];
/** 修改排程工作：參數。 */
export interface ScheduleJobModifyParams
{
	/** 排程工作 ID。 */
	id					: number

	/** 工作種類。 */
	task				?: SCHEDULE_TASK

	/** 工作參數。 */
	parameters			?: string

	/** 排定執行時間。 */
	time				?: string

	/** 狀態。 */
	status				?: SCHEDULE_STATUS
}

//-----------------------------------------------------------------------
// 外部擴充功能相關。

/** 外部擴充功能 - 檢查工作：參數結構定義。 */
export const EXTERNAL_CHECK_TASK_PARAMS: ParamTypeDef[] =
[
	{ name: "key",			type: "string",		acceptNull: false },
];
/** 外部擴充功能 - 檢查工作：參數。 */
export interface ExternalCheckTaskParams
{
	/** 外部擴充功能連線用 key。 */
	key					: string
}
/** 外部擴充功能 - 檢查工作：結果。 */
export interface ExternalCheckTaskRes extends ApiRes
{
	/** 工作種類。 */
	type				: EXTERNAL_TASK_TYPE

	/** 工作參數。 */
	params				: any[]
}

/** 外部擴充功能 - 推送資料：參數結構定義。 */
export const EXTERNAL_PUSH_DATA_PARAMS: ParamTypeDef[] =
[
	{ name: "key",			type: "string",		acceptNull: false },
	{ name: "type",			type: "string"	,	acceptNull: false },
	{ name: "list",			type: "array",		acceptNull: false },
];
/** 外部擴充功能 - 推送資料：參數。 */
export interface ExternalPushDataParams
{
	/** 外部擴充功能連線用 key。 */
	key					: string

	/** 工作種類。 */
	type				: EXTERNAL_TASK_TYPE

	/** 資料列表。 */
	list				: any[]
}

//-----------------------------------------------------------------------
// 統計資料相關。

/** 修改頻道：參數結構定義。 */
export const CHANNEL_MODIFY_PARAMS: ParamTypeDef[] =
[
	{ name: "id",			type: "integer",	acceptNull: false },
	{ name: "kolId",		type: "integer",	acceptNull: true,	optional: true },
	{ name: "keywordIds",	type: "array",		acceptNull: true,	optional: true },
	{ name: "status",		type: "string",		acceptNull: true,	optional: true },
];
/** 修改頻道：參數。 */
export interface ChannelModifyParams
{
	/** 資料來源 ID。 */
	id					: number

	/** 所屬 KOL ID。 */
	kolId				?: number

	/** 關鍵字列表。 */
	keywordIds			?: any[]

	/** 狀態。 */
	status				?: CHANNEL_STATUS
}

/** 修改目標：參數結構定義。 */
export const TARGET_MODIFY_PARAMS: ParamTypeDef[] =
[
	{ name: "id",		type: "integer",	acceptNull: false },
	{ name: "status",	type: "string",		acceptNull: true,	optional: true },
];
/** 修改目標：參數。 */
export interface TargetModifyParams
{
	/** 資料來源 ID。 */
	id					: number

	/** 狀態。 */
	status				?: TARGET_STATUS
}
